import {
  DocumentClassAttributes,
  MaterialAttributes,
  OrderDetailAttributes,
  SubRouteAttributes,
  VisitAttributes,
  WarehouseAttributes,
} from "@albatrosdeveloper/ave-models-package";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/status.enum";
import { CommonModule, DecimalPipe } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import {
  MatPaginatorIntl,
  MatPaginatorModule,
} from "@angular/material/paginator";
import { MatSelectModule } from "@angular/material/select";
import {
  MatSidenavContainer,
  MatSidenavModule,
} from "@angular/material/sidenav";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { FilterSectionComponent } from "app/common/components/filter-section/filter-section.component";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { IPaginationResult, IRequestResult } from "app/common/interfaces";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { ExportOptions } from "app/layout/export-overlay/tasks/core/export-task.interfaces";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { CompanyService } from "app/modules/components/configuration/company/company.service.service";
import { WarehouseService } from "app/modules/components/configuration/warehouse/warehouse.service";
import { BusinessPartnersFiltersComponent } from "app/modules/components/filters/business-partners-filters/business-partners-filters.component";
import { ConfigurationFiltersComponent } from "app/modules/components/filters/configuration-filters/configuration-filters.component";
import { DatesFiltersComponent } from "app/modules/components/filters/dates-filters/dates-filters.component";
import { DocumentsFiltersComponent } from "app/modules/components/filters/documents-filters/documents-filters.component";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { MaterialsManagementFiltersComponent } from "app/modules/components/filters/materials-management-filters/materials-management-filters.component";
import { RoutesFiltersComponent } from "app/modules/components/filters/routes-filters/routes-filters.component";
import { StatusFiltersComponent } from "app/modules/components/filters/status-filters/status-filters.component";
import { MaterialService } from "app/modules/components/material/material/material.service";
import { SubRouteService } from "app/modules/components/routing/sub-routes/sub-route.service";
import { MasterUserInterface } from "app/modules/components/sales/order-generate/interfaces/master-user.interface";
import { OrdersListService } from "app/modules/components/sales/order-list-components/order-list.service";
import { DocumentFlowButtonComponent } from "app/modules/shared/document-flow-button/document-flow-button.component";
import { DecimalFormatPipe } from "app/pipes/decimal-company.pipe";
import { FormatOtherDateHourPipe } from "app/pipes/other-date.pipe";
import { UserRouteDisplayComponent } from "app/shared/components/user-route-display/user-route-display.component";
import { DateHelper } from "app/utils/date.helper";
import { getCode } from "app/utils/method";
import { environment } from "environments/environment.prod";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import {
  debounceTime,
  distinctUntilChanged,
  skip,
  Subject,
  Subscription,
  take,
  takeUntil,
} from "rxjs";
import { ExportDialogComponent } from "../../../../../layout/export-overlay/dialog/export-dialog.component";
import {
  ORDER_CUSTOMER_EXPORT_ID,
  ORDER_CUSTOMER_TITLE,
} from "./exports/order-customer.constants";
import { OrderCustomerExport } from "./exports/order-customer.export";
import { OrderCustomerService } from "./order-customer.service";

// Smart Grid imports
import {
  ColDef,
  GridConfig,
  GridReadyEvent,
  PageChangedEvent,
  SmartGridComponent,
  SortChangedEvent,
} from "app/shared/components/smart-grid";

// Cell Renderers
import { SerialNavigationCellRendererComponent } from 'app/modules/components/reports/exchange/request-change/cell-renderers/serial-navigation-cell-renderer.component';
import { CompanyCurrencyPipe } from "app/pipes/currency.pipe";
import { ActionsCellRendererComponent } from "./cell-renderers/actions-cell-renderer.component";
import { CustomerCellRendererComponent } from "./cell-renderers/customer-cell-renderer.component";
import { DateCellRendererComponent } from "./cell-renderers/date-cell-renderer.component";
import { MaterialCellRendererComponent } from "./cell-renderers/material-cell-renderer.component";
import { QuantityCellRendererComponent } from "./cell-renderers/quantity-cell-renderer.component";
import { RouteUserCellRendererComponent } from "./cell-renderers/route-user-cell-renderer.component";
import { OrderStatusERPEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/order-status-erp.enum";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { CurrencyCellRendererComponent } from "app/shared/components/smart-grid/cell-renderers/currency-cell-renderer/currency-cell-renderer.component";
import { BusinessPartnerCellRendererComponent } from "app/modules/components/monitor/monitor-routes/cell-renderers/business-partner-cell-renderer.component";
import { TripLiquidationCellRendererComponent } from "../../liquidations/cell-renderers/trip-liquidation-cell-renderer.component";
import { TripCellRendererComponent } from "../../visits/visit-report/cell-renderers/trip-cell-renderer.component";

@Component({
  selector: "app-order-customer",
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatSidenavModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    MatDialogModule,
    NgxMatSelectSearchModule,
    FormatOtherDateHourPipe,
    MatAutocompleteModule,
    FilterComponent,
    DecimalFormatPipe,
    DocumentFlowButtonComponent,
    MatMenuModule,
    FilterSectionComponent,
    StatusFiltersComponent,
    MaterialsManagementFiltersComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
    UserRouteDisplayComponent,
    HoverMenuDirective,
    SmartGridComponent,
    CompanyCurrencyPipe,
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    OrderCustomerExport,
    DecimalPipe,
  ],
  templateUrl: "./order-customer.component.html",
  styleUrl: "./order-customer.component.scss",
})
export class OrderCustomerComponent implements OnInit, OnDestroy {
  getCode = getCode;
  private isInitialized = false;

  StatusEnum = StatusEnum;
  OrderStatusERPEnum = OrderStatusERPEnum;
  DocumentTypeEnum = DocumentTypeEnum;
  type = DocumentTypeEnum.PEDIDO;

  // ViewChild for SmartGrid API access
  @ViewChild(SmartGridComponent) smartGrid!: SmartGridComponent<any>;
  displayedColumns: string[] = [
    "serials",
    "shipToExternalKeyCode",
    "materialName",
    "soldQuantity",
    //"baseUnitOfMeasure",
    "unitPrice",
    "total",
    "actions",
  ];

  dataSource = new MatTableDataSource<OrderDetailAttributes>([]);

  totalItems: number = 0;
  searchControl = new FormControl("");
  search: string = "";
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof OrderDetailAttributes = "_id";
  sortDirection: "ASC" | "DESC" = "DESC";

  filter = {
    company: null,
    dateini: new Date(),
    dateend: new Date(),
    warehouseId: null,
    routes: [],
    item: null,
  };

  //active filtro
  isAcive: boolean = true;

  range = new FormGroup({
    start: new FormControl<Date | null>(new Date()),
    end: new FormControl<Date | null>(new Date()),
  });

  warehouseList: WarehouseAttributes[] = [];
  warehouseFilterList: WarehouseAttributes[] = [];
  searchWarehouse = "";

  subRouteList: SubRouteAttributes[] = [];
  listFilterSubRouteList: SubRouteAttributes[] = [];
  searchSubRoute = "";

  UMPActive: boolean;

  // Smart Grid Configuration
  columnDefs: ColDef<any>[] = [
    {
      field: "subRoute.code" as any,
      headerName: "Ruta - Usuario",
      cellRenderer: RouteUserCellRendererComponent,
      autoSize: true,
      minWidth: 300, // Much wider for multi-line content (route, user, responsible)
      sortable: true,
      pinned: "left",
      filterOptions: {
        // Extract subRoute name for filtering and display
        // This single function handles both filter dropdown display AND filtering logic
        valueExtractor: (item: any) => item.subRoute?.name,
        customFilterFn(rowData, filterValue) {
          const subRouteName = rowData.subRoute?.name;
          if (!rowData) return !filterValue;

          return subRouteName === filterValue;
        },
      },
    },
    {
      field: 'trip.id',
      headerName: 'Viaje',
      minWidth: 150,
      sortable: true,
      cellRenderer: TripCellRendererComponent,
      cellRendererParams: {
        onTripClick: (data: any) => this.goToTravel(data)
      }
    },
    {
      field: "serials",
      headerName: "Nro. Doc.",
      cellRenderer: SerialNavigationCellRendererComponent,
      cellRendererParams: {
        documentType: this.type, // 'DT002' (Solicitud de Pedido)
      },
      autoSize: true,
      minWidth: 220, // Wider for document numbers
      sortable: true,
    },
    {
      field: "date",
      headerName: "Fecha",
      cellRenderer: DateCellRendererComponent,
      autoSize: true,
      minWidth: 180, // Wider for full date format
      sortable: true,
      filterOptions: {
        // Format date display in filter dropdown
        valueExtractor(item) {
          if (!item.date) return "";

          const date = new Date(item.date);

          return date.toLocaleDateString("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
        },
        customFilterFn(rowData, filterValue) {
          const rowDate = rowData.date;
          if (!rowDate) return !filterValue;

          const rowDateObj = new Date(rowDate);

          const rowDateStr = rowDateObj.toLocaleDateString("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });

          return rowDateStr === filterValue;
        },
      },
    },
    {
      field: "soldTo.name",
      headerName: "Pagador/Receptor",
      cellRenderer: BusinessPartnerCellRendererComponent,
      autoSize: true,
      minWidth: 250, // Much wider for code + long name
      sortable: true,
      filterOptions: {
        // Display customer name in filter dropdown instead of code
        // Note: Requires shipToName field to be present in row data
        valueExtractor: (item: any) => item?.soldTo?.name,
        customFilterFn(rowData, filterValue) {
          const rowCustomer = rowData?.soldTo?.name;
          if (!rowCustomer) return !filterValue;

          return rowCustomer === filterValue;
        },
      },
    },
    {
      field: "materialName.name" as any,
      headerName: "Material",
      cellRenderer: MaterialCellRendererComponent,
      autoSize: true,
      minWidth: 250, // Much wider for code + long descriptions
      sortable: true,
      filterOptions: {
        // Extract material name for filtering and display
        valueExtractor: (item: any) => item.materialName?.name,
        customFilterFn(rowData, filterValue) {
          const rowMaterial = rowData.materialName?.name;
          if (!rowMaterial) return !filterValue;

          return rowMaterial === filterValue;
        },
      },
    },
    {
      field: "soldQuantity",
      headerName: "Cantidad",
      cellRenderer: QuantityCellRendererComponent,
      cellRendererParams: {
        getUMPActive: () => this.UMPActive,
      },
      autoSize: true,
      minWidth: 280, // Much wider for 3 lines (UMV, UMB, UMP)
      sortable: true,
    },
    {
      field: "unitPrice",
      headerName: "Precio",
      cellRenderer: CurrencyCellRendererComponent,
      autoSize: true,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
    },
    {
      field: "subTotal",
      headerName: "Subtotal",
      cellRenderer: CurrencyCellRendererComponent,
      autoSize: true,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
    },
    {
      field: "total",
      headerName: "Total",
      cellRenderer: CurrencyCellRendererComponent,
      autoSize: true,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
    },
    {
      headerName: "",
      cellRenderer: ActionsCellRendererComponent,
      sortable: false,
      autoSize: true,
      width: 80,
      minWidth: 80, // Wider for action button
      colId: "actions",
      pinned: "right",
    },
  ];

  gridConfig: GridConfig<any> = {
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [{ field: "_id" as any, direction: "desc" }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  materialList: MaterialAttributes[] = [];
  searchMaterial = "";
  private searchSubject = new Subject<string>();

  // selectedDocumentClass: string = '0';
  selectedDocumentClass: string[] = [];
  filteredDocumentClasses: Subject<DocumentClassAttributes[]> = new Subject();
  documentClassMultiFilterCtrl: FormControl = new FormControl();
  isIndeterminateDocumentClass: boolean = false;
  isCheckedDocumentClass: boolean = false;
  private _onDestroyDocumentClass = new Subject<void>();

  selectedSeller: string = "0";
  selectedWithTravel: string[] = [];

  documentClassesList: DocumentClassAttributes[] = [];
  sellerList: MasterUserInterface[] = [];

  selectedSoldTo: string = "0";
  soldToControl = new FormControl("");
  soldToList: any[] = [];
  soldToFilterSelected: any = null;

  selectedShipTo: string = "0";
  shipToControl = new FormControl("");
  shipToList: any[] = [];

  subs: Subscription[] = [];
  ordersToFilter: string[] = [];

  //BUSCADOR
  searchSubscription: Subscription;

  form: FormGroup;

  //Para filtros
  selectedStatuses: number[] = [];

  // Estados ERP
  selectedERPStatuses: number[] = [];

  selectedCustomerType: string[] = [];

  loadConfigData = false;
  loadDocumentsData = false;
  loadRoutesData = false;
  loadBusinessPartnersData = false;
  loadMaterialsData = false;

  constructor(
    private orderCustomer: OrderCustomerService,
    private readonly warehouseService: WarehouseService,
    private readonly subRouteService: SubRouteService,
    private readonly companyService: CompanyService,
    private ordersListService: OrdersListService,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private orderCustomerExport: OrderCustomerExport,
    private readonly materialService: MaterialService,
    private readonly searchService: SearchService,
    private activatedRoute: ActivatedRoute,
    private companyCurrencyPipe: CompanyCurrencyPipe,
    private fb: FormBuilder,
    private readonly router: Router
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      startTrip: [],
      endTrip: [],
      companyId: [],
      salesOfficeId: [],
      documentClassId: [],
      subRouteId: [],
      assignedUserId: [],
      payingBusinessPartnerId: [],
      receivingBusinessPartnerId: [],
      warehouseId: [],
      end_startTrip: [],
      end_endTrip: [],
      soldToControl: [],
      searchReceivingBusinessPartner: [],
      searchAssignedUser: [],
      materialGroupId: [[]],
      materialId: [],
      startDeliveryDate: [],
      endDeliveryDate: [],
      pickerUpload_start: [],
      pickerUpload_end: [],
    });
  }

  goToTravel(element: VisitAttributes) {
        if (element.trip?.id) {
          const queryParams = { tripId: element.trip.id };
          const urlTree = this.router.createUrlTree(['monitor', 'principal-screen'], { queryParams });
          const absoluteUrl = window.location.origin + this.router.serializeUrl(urlTree);
          window.open(absoluteUrl, '_blank');
        }
      }

  onConfigExpanded(): void {
    this.loadConfigData = true;
  }

  onDocumentsExpanded(): void {
    this.loadDocumentsData = true;
  }

  onRoutesExpanded(): void {
    this.loadRoutesData = true;
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  onMaterialsExpanded(): void {
    this.loadMaterialsData = true;
  }

  ngOnInit(): void {
    this.subs.push(
      this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
        if (params["code"]) {
          this.ordersToFilter = params["code"] ? params["code"].split(",") : [];
        }

        // pickerUpload_end
      if (params["pickerUpload_start"]) {
        this.form.patchValue({
          pickerUpload_start: new Date(params["pickerUpload_start"])
        });
      }

      // pickerUpload_end
      if (params["pickerUpload_end"]) {
        this.form.patchValue({
          pickerUpload_end: new Date(params["pickerUpload_end"])
        });
      }

        this.fetchCompanies();
        this.isInitialized = true;
      })
    );
    this.onHandleSearch();
  }

  ngAfterViewInit(): void {
    this.documentClassMultiFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroyDocumentClass))
      .subscribe(() => {
        this.filterDocumentClasses();
        this.setToggleAllCheckboxStateDocumentClass();
      });

    this.searchControl.valueChanges
      .pipe(skip(1), debounceTime(500), distinctUntilChanged())
      .subscribe((filterValue: string) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = filterValue;
          this.onFilter();
        }
      });

    this.soldToControl.valueChanges
      .pipe(skip(1), debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if (this.isInitialized) {
          if (
            typeof value === "string" &&
            value.trim().length === 24 &&
            /^[a-f0-9]{24}$/.test(value)
          ) {
            return;
          }
          this.soldToList = [];
          this.soldToFilterSelected = null;
          this.getListSoldTo(value);

          this.getListReport();
        }
      });

    this.shipToControl.valueChanges
      .pipe(skip(1), debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if (this.isInitialized) {
          if (
            typeof value === "string" &&
            value.trim().length === 24 &&
            /^[a-f0-9]{24}$/.test(value)
          ) {
            return;
          }

          this.shipToList = [];
          this.getListShipTo(value);
          this.getListReport();
        }
      });

    this.setupMaterialSearchControl();
    this.getMaterialList();
  }

  private filterDocumentClasses(): void {
    if (!this.documentClassesList) {
      return;
    }
    let search = this.documentClassMultiFilterCtrl.value;
    if (!search) {
      this.filteredDocumentClasses.next(this.documentClassesList);
      return;
    } else {
      search = search.toLowerCase();
    }
    const filtered = this.documentClassesList.filter((docClass) =>
      docClass.name.toLowerCase().includes(search)
    );
    this.filteredDocumentClasses.next(filtered);
  }

  private setToggleAllCheckboxStateDocumentClass(): void {
    const selectedCount = this.selectedDocumentClass.length;
    const totalCount = this.documentClassesList.length;

    this.isCheckedDocumentClass =
      selectedCount > 0 && selectedCount === totalCount;
    this.isIndeterminateDocumentClass =
      selectedCount > 0 && selectedCount < totalCount;
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .pipe(skip(1), debounceTime(300))
      .subscribe((term) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = term;
          this.onFilter();
        }
      });
  }

  onFilter() {
    this.range.valueChanges.subscribe((rangeValues) => {
      this.filter.dateini = rangeValues.start || this.filter.dateini;
      this.filter.dateend = rangeValues.end || this.filter.dateend;
    });

    this.getListReport();
  }

  onClearFilter() {
    this.filter = {
      company: this.filter.company,
      dateini: new Date(),
      dateend: new Date(),
      warehouseId: null,
      routes: [],
      item: null,
    };
    this.range.reset({
      start: new Date(),
      end: new Date(),
    });
    const today = new Date();
    this.form.reset({ start: today, end: today });
    this.onFilter();
  }

  applyFilter(filterValue: string) {
    this.pageIndex = 1;
    this.search = filterValue;
    this.getListReport();
  }

  /**
   * Smart Grid: Handle grid ready event
   * Called when grid is fully initialized and rendered
   */
  onGridReady(event: GridReadyEvent<any>): void {
    // Auto-size all columns after grid is ready
    setTimeout(() => {
      event.api.autoSizeAllColumns();
    }, 100);
  }

  /**
   * Smart Grid: Handle page change event (API mode)
   */
  onPageChanged(event: PageChangedEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.getListReport();
  }

  /**
   * Smart Grid: Handle sort change event (API mode)
   */
  onSortChanged(event: SortChangedEvent<any>): void {
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as any;
      this.sortDirection = sortState.direction.toUpperCase() as "ASC" | "DESC";
    } else {
      this.sortActive = "_id";
      this.sortDirection = "DESC";
    }
    this.getListReport();
  }

  /**
   * Legacy mat-table methods (kept for compatibility)
   */
  changeSort(e) {
    this.sortActive = e.active;
    this.sortDirection = e.direction?.toUpperCase() || "ASC";
    this.getListReport();
  }

  changePage(e) {
    this.pageIndex = e.pageIndex + 1;
    this.pageSize = e.pageSize;
    this.getListReport();
  }

  getListReport() {
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: this.sortDirection,
    };

    const hasOrderIds = this.ordersToFilter && this.ordersToFilter.length > 0;
    const hasSearch = this.search && this.search.trim().length > 0;

    let filter: IFilters = {
      type: this.type,
      startDate:
        !hasOrderIds && this.form.get("start")?.value
          ? DateHelper.getStartOfDay(this.form.get("start").value)
          : null,
      endDate:
        !hasOrderIds && this.form.get("end")?.value
          ? DateHelper.getEndOfDay(this.form.get("end").value)
          : !hasOrderIds && this.form.get("start")?.value
            ? DateHelper.getEndOfDay(this.form.get("start").value)
            : null,
      startTrip:
        !hasOrderIds && this.form.get("startTrip")?.value
          ? DateHelper.getStartOfDay(this.form.get("startTrip").value)
          : null,
      endTrip:
        !hasOrderIds && this.form.get("endTrip")?.value
          ? DateHelper.getEndOfDay(this.form.get("endTrip").value)
          : !hasOrderIds && this.form.get("startTrip")?.value
            ? DateHelper.getEndOfDay(this.form.get("startTrip").value)
            : null,
      end_startTrip:
        !hasOrderIds && this.form.get("end_startTrip")?.value
          ? DateHelper.getStartOfDay(this.form.get("end_startTrip").value)
          : null,
      end_endTrip:
        !hasOrderIds && this.form.get("end_endTrip")?.value
          ? DateHelper.getEndOfDay(this.form.get("end_endTrip").value)
          : !hasOrderIds && this.form.get("end_startTrip")?.value
            ? DateHelper.getEndOfDay(this.form.get("end_startTrip").value)
            : null,
      startDeliveryDate:
        !hasOrderIds && this.form.get("startDeliveryDate")?.value
          ? DateHelper.getStartOfDay(this.form.get("startDeliveryDate").value)
          : null,
      endDeliveryDate:
        !hasOrderIds && this.form.get("endDeliveryDate")?.value
          ? DateHelper.getEndOfDay(this.form.get("endDeliveryDate").value)
          : !hasOrderIds && this.form.get("startDeliveryDate")?.value
            ? DateHelper.getEndOfDay(this.form.get("startDeliveryDate").value)
            : null,
      pickerUpload_start:
         this.form.get("pickerUpload_start")?.value
          ? DateHelper.getStartOfDay(this.form.get("pickerUpload_start").value)
          : null,
      pickerUpload_end:
         this.form.get("pickerUpload_end")?.value
          ? DateHelper.getEndOfDay(this.form.get("pickerUpload_end").value)
          : this.form.get("pickerUpload_start")?.value
            ? DateHelper.getEndOfDay(this.form.get("pickerUpload_start").value)
            : null,
      companyId: this.form.get("companyId")?.value,
      salesOfficeId: this.form.get("salesOfficeId")?.value,
      warehouseId: this.form.get("warehouseId")?.value,
      documentClass: this.form.get("documentClassId")?.value,
      subRouteId: this.form.get("subRouteId")?.value,
      soldTo: this.form.get("payingBusinessPartnerId")?.value,
      shipTo: this.form.get("receivingBusinessPartnerId")?.value,
      seller: this.form.get("assignedUserId")?.value,
      status:
        !hasOrderIds && this.selectedStatuses?.length > 0
          ? this.selectedStatuses
          : null,
      statusERP: this.selectedERPStatuses.length
        ? this.selectedERPStatuses
        : null,
      typeCustomer: this.selectedCustomerType.length
        ? this.selectedCustomerType
        : null,
      materialGroupId: this.form.get("materialGroupId").value,
      materialId:
        this.ordersToFilter && this.ordersToFilter.length > 0
          ? this.ordersToFilter
          : this.form.get("materialId").value,
      ordersToFilter: hasOrderIds && !hasSearch ? this.ordersToFilter : null,
    };

    if (hasOrderIds && !hasSearch) {
      this.ordersToFilter = null;
    }
    this.orderCustomer.findAllWithCount(options, filter).subscribe({
      next: (res: IPaginationResult<OrderDetailAttributes>) => {
        this.dataSource.data = res?.data?.[0]?.data ?? [];
        this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener reporte",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  private getSortField(): keyof OrderDetailAttributes {

    return this.sortActive;
  }

  //filter
  private fetchCompanies(): void {
    this.companyService.findToComboBox().subscribe((res) => {
      if (res.data.length === 1) {
        this.UMPActive = res.data[0].UMPActive;
        this.companyService.setActiveCompany(res.data[0]);
      }
    });
  }

  private fetchAllWarehouses(): void {
    this.warehouseService.findToComboBox().subscribe({
      next: (res) => {
        this.warehouseList = res.data;
        this.warehouseFilterList = this.warehouseList.slice();
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error fetching warehouses",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  changueOptionsWarehouse(event) {
    if (!this.warehouseList) {
      return;
    }
    let search = event;
    if (!search) {
      this.warehouseFilterList = this.warehouseList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.warehouseFilterList = this.warehouseList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  changeWarehouse(event): void {
    this.subRouteList = [];
    this.listFilterSubRouteList = [];
    this.getAllRutas();
  }

  private getAllRutas(): void {
    this.subRouteService
      .findToWarehouse(this.filter.warehouseId ? [this.filter.warehouseId] : [])
      .subscribe({
        next: (res) => {
          this.subRouteList = res.data;
          this.listFilterSubRouteList = this.subRouteList.slice();
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error fetching route",
            "Cerrar",
            { duration: 5000 }
          );
        },
      });
  }

  changueOptionsSubRoute(event) {
    if (!this.subRouteList) {
      return;
    }
    let search = event;
    if (!search) {
      this.listFilterSubRouteList = this.subRouteList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.listFilterSubRouteList = this.subRouteList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  onExportButtonPressed() {
    if (!this.orderCustomerExport.canStartTask()) return;
    const dialogRef = this.matDialog.open(ExportDialogComponent, {
      data: { title: ORDER_CUSTOMER_TITLE },
    });
    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.orderCustomerExport.export(
          options.format,
          options.range,
          this.totalItems,
          this.pageSize,
          this.pageIndex,
          ORDER_CUSTOMER_EXPORT_ID,
          ORDER_CUSTOMER_TITLE
        );
      }
    });
  }

  onDocumentClassSelectionChange(selectedValues: string[]): void {
    this.selectedDocumentClass = selectedValues;
  }

  private getListSeller() {
    this.ordersListService.fetchMasterUsers().subscribe({
      next: (res: IRequestResult<MasterUserInterface[]>) => {
        if (res.status == "success") {
          this.sellerList = res.data;
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error fetching sellers",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  getListSoldTo(search?): void {
    let dataToSend = {
      companyIds: [environment.companyId],
      search: search?.toString() || "",
    };
    this.ordersListService.fetchSoldTos(dataToSend).subscribe((res) => {
      this.soldToList = res?.data;
      this.cdr.detectChanges();
    });
  }

  onChangeSoldTo() {
    this.soldToFilterSelected = this.soldToList.find(
      (stl) => stl.id == this.soldToControl.value
    );
    this.shipToControl.setValue("");
    this.getListShipTo();
  }

  getListShipTo(search?): void {
    this.shipToList = [];
    const soldToValue = this.soldToFilterSelected
      ? this.soldToFilterSelected._id
      : null;

    let dataToSend: any = {
      companyIds: [environment.companyId],
      search: search?.toString() || "",
    };

    if (soldToValue) {
      dataToSend.soldToMongoId = soldToValue;
    }

    this.ordersListService.fetchShipTos(dataToSend).subscribe((res) => {
      this.shipToList = res?.data;
      this.cdr.detectChanges();
    });
  }

  fetchShipToName = (shipToId: string): string => {
    const foundShipTo = this.shipToList.find(
      (shipTo) => shipTo.id === shipToId
    );
    if (foundShipTo) {
      return foundShipTo.name;
    }
    return "";
  };

  fetchSoldToName = (soldtoId: string): string => {
    const foundSoldTo = this.soldToList.find(
      (soldTo) => soldTo.id === soldtoId
    );
    if (foundSoldTo) {
      return foundSoldTo.name;
    }
    return "";
  };

  private setupMaterialSearchControl(): void {
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        if (searchTerm) {
          this.searchMaterial = searchTerm;
          this.getMaterialList();
        }
      });
  }

  changueOptionsMaterial(event) {
    this.searchSubject.next(event);
  }

  private getMaterialList(): void {
    const materialOptions = {
      search: this.searchMaterial ? this.searchMaterial : null,
      currentPage: 1,
      limitPerPage: 50,
      sortActive: "name",
      //sortDirection: "ASC"
    };

    this.materialService
      .findAllWithCount(
        materialOptions,
        this.filter.company ? this.filter.company : []
      )
      .subscribe({
        next: (res: IPaginationResult<MaterialAttributes>) => {
          this.materialList = res?.data?.[0]?.data ?? [];
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al traer registros",
            "Cerrar",
            { duration: 3000 }
          );
        },
      });
  }

  getActiveFiler() {
    this.isAcive = environment.activeFilter;
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
